import thunkMiddleware from 'redux-thunk';
import { getPageInfo } from './requests/requestsSelectors';
import { _isString, _objArray, _isArray, _clone, _trim, _matchRequest, _queryString } from './utils';
import { _getURL, PAGE_SIZE } from '../config';
import createLogger from '../utils/createLogger';

const DEBUG = true;
const debug = createLogger(DEBUG, `middlewares.js`);

const headers = new Headers();
headers.append(`Access-Control-Allow-Origin`, `*`);

const initialPage = {
  offset: -1,
  count: PAGE_SIZE,
  total: undefined,
};


const _buildRequestFromAction = (action, state) => {
  let { type, id, ...args } = Object.assign({}, _trim(action));
  let request = _matchRequest(type);

  // if not request, return falsey
  if (!request || request.state !== `REQUEST`)
    return null;
  if(!request.headers) request.headers = {};

  debug.log(`  building request from action:`, action);

  if (request.paged) {
    let prevPageInfo = getPageInfo(request.name, state) || _clone(initialPage);
    debug.log(`    prevPageInfo:`, prevPageInfo);
    request.offset = prevPageInfo.offset < 0 ? 0
      : prevPageInfo.offset + prevPageInfo.count;
    request.count = prevPageInfo.count;
    request.total = prevPageInfo.total;
    args.offset = request.offset;
    args.count = request.count;
  }

  // build uri (with ID if supplied)
  let uri = `${_getURL()}/api/${request.route}${!!id ? `/${id}` : ``}`;
  let body_blob = null;

  debug.log(`    uri: ${uri}`);

  debug.log(`    parsing args:`, args);

  if (args.__manual) {
    // manual request: action contains query string/dict and body Blob/FormData/dict
    if (!!args.query) {
      if (typeof args.query === 'string')       // if string, use directly
        request.url += args.query;
      else if (typeof args.query === 'object')  // if dict, encode to query string first
        request.url += _queryString(args.query);
    }
    if (!!args.body) {
      if (args.body instanceof Blob ||
          args.body instanceof FormData)       // if already Blob or FormData, use directly
        request.body = args.body;
      else if (typeof args.body === 'object')   // if dict, encode to Blob by default
        request.body = new Blob([
          JSON.stringify(args.body, null, 2)
        ], { type: 'application/json' })
    }
  } else {

    // translate remaining args onto request
    switch (request.method) {
      case 'GET':                   // for GET, append each arg to URL query

        let argv = _objArray (args || {});
        if (argv.length > 0) {
          uri += `?`;
          argv.forEach (([key, val]) => {
            if (_isArray (val)) { // TODO match w/ api format
              uri += `${key}=`;
              val.forEach ((v) => uri += `${v}%2C`);
              uri = uri.slice (0, uri.length - 3);
              uri += `&`;
            } else {
              uri += `${key}=${val}&`
            }
          });
          uri = uri.slice (0, uri.length - 1);
        }
        break;

      case `PUT`:                   // for PUT, add all args to body
        body_blob = new Blob ([
          JSON.stringify (args, null, 2),
        ], {type: 'application/json'});
        break;

      case `POST`:                  // for POST, add all args to body
        body_blob = new Blob ([
          JSON.stringify (args, null, 2),
        ], {type: 'application/json'});
        break;

      case `DELETE`:                // for DELETE, no args are needed
        argv = _objArray (args || {});
        if (argv.length > 0) {
          uri += `?`;
          argv.forEach (([key, val]) => {
            if (_isArray (val)) { // TODO match w/ api format
              uri += `${key}=`;
              val.forEach ((v) => uri += `${v}%2C`);
              uri = uri.slice (0, uri.length - 3);
              uri += `&`;
            } else {
              uri += `${key}=${val}&`
            }
          });
          uri = uri.slice (0, uri.length - 1);
        }
        break;
      default:
        break;
    }

    request.body = body_blob;
  }
  request.uri = uri;
  request.parseResponseToAction = _buildActionFromResponse(request);

  try {
      request.headers['USER'] = state.auth.session.idTokenPayload.email || state.auth.session.idTokenPayload.name;
  } catch(err) {
      debug.log(` cannot add user header: ${err}`);
  } 

  debug.log(`  built request:`, request);
  return request;
};



const _buildActionFromResponse = (request) => (error, response) => {
  debug.log(`  creating action from response:`, {request, error, response});

  let action = !error && !!response
    ? _trim({
      type: `${request.name}_SUCCESS`,
      data: response.data || response,
      offset: response.offset !== undefined ? response.offset : request.offset,
      count: response.count !== undefined ? response.count : request.count,
      total: response.total !== undefined ? response.total : request.total,
      original_request: request})
    : _trim({
      type: `${request.name}_FAILURE`,
      original_request: request,
      error: error || null
    });

  debug.log(`  built action:`, action);
  return action;
};


const apiMiddleware = (fetchImpl) => (store) => (next) => (action) => {
  let request = _buildRequestFromAction(action, store.getState());

  if (!request)
    return next(action);

  debug.enter(`API request: ${request.method}: ${request.name} -> ${request.uri}`);
  if (request.paged) {
   action.offset = request.offset;
    action.count = request.count;
    action.total = request.total;
  }

  next(action);

  return fetchImpl(request.uri, {
    method: request.method,
    body: request.body,
    headers: request.headers
  }).then((res) => {
    debug.log(`  > ${request.name} response:`, res);

    if (res.ok) {
      return res.json();
    } else {
      store.dispatch(request.parseResponseToAction(res.statusText));
      return Promise.reject({ fromResponse: true, res });
    }
  }).then((data_raw) => {
    let data = _isString(data_raw) ? JSON.parse(data_raw) : data_raw;

    debug.log(`    data:`, data);

    store.dispatch(request.parseResponseToAction(null, data));
    return Promise.resolve(data);

  }, (error) => {
    debug.log(`    error:`, error);
    if(error.fromResponse)
      return Promise.reject(error.response);

    debug.log('Error not from response', error);
    throw Error(error);
  });
};


export {
  thunkMiddleware,
  apiMiddleware,
}



// WEBPACK FOOTER //
// ./src/data/middlewares.js